// "Will this device receive a command right now?" — asked of the device itself.
//
// WHY NOT THE STATUS TABLE. GetDeviceStatusbyUniqueNumber reports when a device
// last wrote a row, and that answer is only as good as the server's bookkeeping
// and the server's clock. Verified against the live broker on 2026-07-29:
//
//     884811   status row stamped a few minutes ago  → answered in <1s
//     7391679  status row months old                 → silence
//     1134667  status row months old                 → silence
//
// so the table is not WRONG, but it is second-hand, and it is stamped in the
// server's local time while labelled UTC (see deviceStatus.js), which puts every
// row five and a half hours in the future. A liveness rule built on comparing
// that against the phone's clock is measuring the timezone gap as much as the
// device.
//
// An MQTT round-trip has none of those problems. It asks the actual question —
// can I reach this device — over the actual channel the command will use, and it
// needs no clock at all on either side.
//
// ⚠️ THE PROBE IS `S05`, NOT `getallwifi` (2026-10-21). getallwifi made OtaForTuya
// (#9) and OtaForSwitchingNewDesign (#8) do a BLOCKING HTTP POST to the server
// (AddDeviceNetworkDetailsNew, up to 3 s) BEFORE answering — and their other HTTP
// calls block loop() too ("several of these run per cycle"). With the server slow
// the answer missed the window twice and healthy devices went red: 1073599 "ac"
// and 939428 "WASHROOM", which answered a hand-sent probe in 0.1 s. S05 is a pure
// read on all three firmwares (#1, #8, #9 and the C3 1.1.x) — it publishes the
// "[uno]WS,…" line with no HTTP — measured 0.0–0.1 s on every one. getallwifi is
// kept as the SECOND ask (confirmLive): a different payload also passes the
// firmware's 2 s "same message repeated" guard, so an ask that collided with
// someone else's identical one still gets through.

import {
  connectMqtt, subscribeTopic, unsubscribeTopic, addMessageListener,
  publishMessage, waitForConnect, lastOfflineAt,
} from '../mqtt/mqttClient';
import { CMD_GET_ALL_WIFI, CMD_S05 } from './commands';

// How long to wait for replies. ⚠️ 3.5 s WAS TOO SHORT (measured 2026-10-19 on
// the live estate): asked one at a time, healthy devices answered in up to 3.4 s
// (800835), the three-phase meter 801038 in 3.2 s while busy posting readings;
// asked in a burst of 166, the slowest tenth took 2.6–9.6 s. Every one of those
// was painted red "not answering" while working. 8 s covers them; a reply still
// ends the wait at once, so a healthy device costs no more than before.
export const PROBE_WINDOW_MS = 8000;

// ⚠️ NEVER A BURST. A room or location page mounts every card at once, and each
// card asked its device in the same instant — in a burst of 166 probes, 13
// healthy devices missed the window (same day, same devices: all answered when
// asked alone). So single probes queue: at most MAX_IN_FLIGHT at a time, started
// PROBE_SPACING_MS apart.
const MAX_IN_FLIGHT = 3;
const PROBE_SPACING_MS = 150;
let inFlight = 0;
let lastStart = 0;
let pumpTimer = null;
const waiting = [];
// eslint-disable-next-line no-use-before-define
const release = () => { inFlight -= 1; pump(); };
function pump() {
  pumpTimer = null;
  while (inFlight < MAX_IN_FLIGHT && waiting.length) {
    const wait = lastStart + PROBE_SPACING_MS - Date.now();
    if (wait > 0) {
      if (!pumpTimer) pumpTimer = setTimeout(pump, wait);
      return;
    }
    const job = waiting.shift();
    inFlight += 1;
    lastStart = Date.now();
    job().finally(release);
  }
}
/** Run fn() when a probe slot is free. -> its promise */
function queued(fn) {
  return new Promise((resolve, reject) => {
    waiting.push(() => fn().then(resolve, reject));
    pump();
  });
}

// Gap between publishes when probing many devices. Each reply also makes the
// server rewrite that device's stored network list, so a location's worth of
// devices is spread out rather than fired as one burst.
export const PROBE_STEP_MS = 40;

const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

/**
 * Ask a set of devices whether they are there.
 *
 * Returns { live: Set<string>, reachedBroker: boolean }. `reachedBroker` is the
 * part callers must not ignore: if the broker could not be reached, NOTHING
 * answered, and reporting every device as offline would be an artefact of our
 * own connection rather than a fact about the hardware.
 */
export async function probeLiveness(uniqueNumbers, windowMs = PROBE_WINDOW_MS) {
  const list = [...new Set((uniqueNumbers || []).map(String).filter(Boolean))];
  const live = new Set();
  if (!list.length) return { live, reachedBroker: true };

  connectMqtt();
  const reachedBroker = await waitForConnect();
  if (!reachedBroker) return { live, reachedBroker: false };

  const wanted = new Set(list);
  // Listen before publishing — see waitForConnect's note on why the order here
  // is not cosmetic.
  const detach = addMessageListener((topic) => {
    const t = String(topic);
    if (!t.endsWith('/client')) return;      // our own publish echoes on /device
    const uno = t.slice(0, t.indexOf('/'));
    if (wanted.has(uno)) live.add(uno);
  });

  list.forEach((uno) => subscribeTopic(`${uno}/client`));

  for (let i = 0; i < list.length; i += 1) {
    publishMessage(`${list[i]}/device`, CMD_S05);
    // eslint-disable-next-line no-await-in-loop
    if (i < list.length - 1) await sleep(PROBE_STEP_MS);
  }

  await sleep(windowMs);
  detach();
  list.forEach((uno) => unsubscribeTopic(`${uno}/client`));

  return { live, reachedBroker: true };
}

/**
 * One device, answered as soon as it replies — the status dot's "check now".
 * -> { ok: true, ms } | { ok: false } (no reply in windowMs) | { ok: null } (no broker)
 *
 * Same read as probeLiveness (getallwifi). ⚠️ A green dot means "heard from in
 * the last 20 minutes" — an ESP8684 posts a network ping every 3 minutes over
 * HTTP whether or not its MQTT link works, so green can sit on a device that
 * will not take a command. This is the only check of THAT (2026-10-17, 16091206).
 */
export function probeOne(uniqueNumber, windowMs = PROBE_WINDOW_MS, command = CMD_S05) {
  return queued(() => probeNow(uniqueNumber, windowMs, command));
}

async function probeNow(uniqueNumber, windowMs, command = CMD_S05) {
  const uno = String(uniqueNumber || '').trim();
  if (!uno) return { ok: false };
  connectMqtt();
  if (!(await waitForConnect())) return { ok: null };
  const topic = `${uno}/client`;
  return new Promise((resolve) => {
    let done = false;
    let timer = null;
    let detach = () => {};
    const started = Date.now();
    const finish = (r) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      detach();
      unsubscribeTopic(topic);
      resolve(r);
    };
    detach = addMessageListener((t) => {
      if (String(t) === topic) finish({ ok: true, ms: Date.now() - started });
    });
    subscribeTopic(topic);
    publishMessage(`${uno}/device`, command);
    timer = setTimeout(() => finish({ ok: false }), windowMs);
  });
}

// ── Asking instead of guessing (owner, 2026-10-17) ──────────────────────────
// ⚠️ NO TIMESTAMP ANSWERS "WILL IT TAKE A COMMAND". LastUpdatedDateTime is kept
// fresh by the device's HTTP network ping even with its MQTT link dead (a device
// "30 min ago" on the details page stayed green), and LastMqttPing moves only on a
// status change (OtaForTuya.ino's MQTT ping is commented out), so an idle healthy
// device has an old one. So a dot that would be GREEN, for a device this page has
// not heard over MQTT in 10 min, asks it once (confirmLive, cached 3 min, shared
// by every card); no answer = red. The first rule below (10–20 min) is kept.
//
// ── The grey zone: heard 10–20 minutes ago ─────────────
// A healthy device reads 5–10 minutes stale (the server's batch refresh,
// deviceStatus.ONLINE_WINDOW_MS) and a dead one 20+. Between 10 and 20 the age
// alone cannot tell — 3250235 sat at "14 min ago", green, not answering. So in
// that band ONLY, the device is asked (probeOne) — once per device per 2 minutes,
// shared by every card showing it — and the answer decides green or red.
// Healthy devices (under 10 min) are never probed.
export const SURE_ONLINE_MS = 10 * 60 * 1000;
const CONFIRM_TTL_MS = 3 * 60 * 1000;
const confirmCache = new Map();   // uno -> { at, promise<boolean|null> }

/** In the grey zone? (age in ms, as deviceStatus.ageOf gives it) */
export const inGreyZone = (ageMs, windowMs) => ageMs >= SURE_ONLINE_MS && ageMs < windowMs;

// ── A stale stamp is not proof of absence either (2026-10-19) ────────────────
// The dot used to paint red WITHOUT ASKING once the server stamp was 20 min old.
// Current sensors (16, 82) post readings to CurrentSense, which never touches
// that stamp — their heartbeat arrives 10 min to 5 h apart — so 801038 and
// 887280 sat red at "107m" / "312m" while answering MQTT in 0.1–3 s. So a device
// whose stamp is under STALE_ASK_MS old is asked too. One silent for longer is
// not: weeks of nothing, and asking it on every page would be noise.
export const STALE_ASK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * -> true (answered) | false (no answer, TWICE) | null (no broker — cannot tell).
 * Cached 3 minutes and shared by every card. A first silence is asked again
 * before it counts — one lost reply is not a dead device.
 */
export function confirmLive(uniqueNumber, windowMs = PROBE_WINDOW_MS) {
  const uno = String(uniqueNumber || '').trim();
  if (!uno) return Promise.resolve(null);
  const hit = confirmCache.get(uno);
  // A "Device Offline" last will since the answer was cached voids it.
  const wentOffline = (lastOfflineAt(uno) || 0) > (hit ? hit.at : 0);
  if (hit && !wentOffline && Date.now() - hit.at < CONFIRM_TTL_MS) return hit.promise;
  const promise = probeOne(uno, windowMs)
    // Second ask: getallwifi — another payload (passes the 2 s repeat guard).
    .then((r) => (r.ok === false ? probeOne(uno, windowMs, CMD_GET_ALL_WIFI) : r))
    .then((r) => r.ok)
    .catch(() => null);
  confirmCache.set(uno, { at: Date.now(), promise });
  return promise;
}
